import React from 'react';
import {
    List,
    TextField,
    ReferenceInput,
    AutocompleteInput,
    Filter,
    DateField,
    SelectInput,
    ShowButton,
    FunctionField,
    EditButton,
} from 'react-admin';

import { StorageRecordStatus, StorageRecordType } from '../constants';
import BoxitChipField from '../components/BoxitChipField';
import { SelectArrayInput } from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';

const StorageRecordFilter = props => (
    <Filter variant="outlined" {...props}>
        <SelectArrayInput
            style={{ minWidth: '10rem' }}
            source="status"
            choices={Object.keys(StorageRecordStatus).map(c => ({
                id: c,
                name: c,
            }))}
        />
        <ReferenceInput
            source="storageId"
            reference="storages"
            resource="storages"
            label="Storage"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText="code"
            />
        </ReferenceInput>
        <ReferenceInput
            source="warehouseId"
            reference="warehouses"
            resource="warehouses"
            label="Warehouse"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
            />
        </ReferenceInput>
        <ReferenceInput
            source="locationId"
            reference="locations"
            resource="locations"
            label="Locations"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText="path"
            />
        </ReferenceInput>
        <SelectInput
            source="type"
            choices={Object.keys(StorageRecordType).map(c => ({
                id: c,
                name: c,
            }))}
        />
    </Filter>
);

// Custom field components
const OrderField = ({ record }) => {
    if (!record.orderId) return null;
    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                window.location.href = `/#/orders/${record.orderId}/show`;
            }}
        >
            {record.orderId}
        </button>
    );
};

const RequestField = ({ record }) => {
    if (!record.requestId) return null;
    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                window.location.href = `/#/requests/${record.requestId}/show`;
            }}
        >
            {record.requestId}
        </button>
    );
};

const StorageField = ({ record }) => {
    if (!record.storageId) return null;
    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                window.location.href = `/#/storages/${record.storageId}/show`;
            }}
        >
            {record.storage?.code || record.storageId}
        </button>
    );
};

const WarehouseField = ({ record }) => {
    if (!record.warehouseId) return null;
    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                window.location.href = `/#/warehouses/${record.warehouseId}/show`;
            }}
        >
            {record.warehouse?.name ||
                record.warehouseName ||
                record.warehouseId}
        </button>
    );
};

const LocationField = ({ record }) => {
    if (!record.locationId) return null;
    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                window.location.href = `/#/locations/${record.locationId}/show`;
            }}
        >
            {record.location?.path || record.locationPath || record.locationId}
        </button>
    );
};

const InByField = ({ record }) => {
    const inBy = record.inBy;
    const inByName = record.inByIdName;
    const inById = record.inById;

    if (!inBy && !inByName) return null;

    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                const userId = inBy?.id || inById;
                if (userId) {
                    window.location.href = `/#/users/${userId}/show`;
                }
            }}
        >
            {inBy?.name || inByName}
        </button>
    );
};

const OutByField = ({ record }) => {
    const outBy = record.outBy;
    const outByName = record.outByIdName;
    const outById = record.outById;

    if (!outBy && !outByName) return null;

    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                const userId = outBy?.id || outById;
                if (userId) {
                    window.location.href = `/#/users/${userId}/show`;
                }
            }}
        >
            {outBy?.name || outByName}
        </button>
    );
};

const StoredByField = ({ record }) => {
    if (!record.activeById) return null;

    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                window.location.href = `/#/users/${record.activeById}/show`;
            }}
        >
            {record.activeByIdName || 'View'}
        </button>
    );
};

const StorageRecordList = props => {
    // Define default columns (visible by default) - matching original StorageRecordList
    const defaultColumns: ColumnConfig[] = [
        { id: 'serial', label: 'Serial', visible: true, order: 1 },
        { id: 'id', label: 'ID', visible: true, order: 2 },
        { id: 'order', label: 'Order', visible: true, order: 3 },
        { id: 'requestId', label: 'Request ID', visible: true, order: 4 },
        { id: 'storage', label: 'Storage', visible: true, order: 5 },
        { id: 'warehouse', label: 'Warehouse', visible: true, order: 6 },
        { id: 'from', label: 'IN', visible: true, order: 7 },
        { id: 'to', label: 'OUT', visible: true, order: 8 },
        { id: 'location', label: 'Location', visible: true, order: 9 },
        { id: 'status', label: 'Status', visible: true, order: 10 },
        { id: 'activeAt', label: 'Stored At', visible: true, order: 11 },
        { id: 'inBy', label: 'In By', visible: true, order: 12 },
        { id: 'storedBy', label: 'Stored By', visible: true, order: 13 },
        { id: 'outBy', label: 'Out By', visible: true, order: 14 },
        { id: 'show', label: 'Actions', visible: true, order: 15 },
    ];

    // Define all available columns (including optional ones)
    const allColumns: ColumnConfig[] = [
        ...defaultColumns,
        // Optional columns (hidden by default)
        { id: 'type', label: 'Type', visible: false, order: 15 },
        { id: 'qty', label: 'Quantity', visible: false, order: 16 },
        { id: 'createdAt', label: 'Created At', visible: false, order: 17 },
        { id: 'updatedAt', label: 'Updated At', visible: false, order: 18 },
        { id: 'storageId', label: 'Storage ID', visible: false, order: 19 },
        { id: 'orderId', label: 'Order ID', visible: false, order: 20 },
        { id: 'warehouseId', label: 'Warehouse ID', visible: false, order: 21 },
        { id: 'locationId', label: 'Location ID', visible: false, order: 22 },
        { id: 'countryId', label: 'Country ID', visible: false, order: 23 },
        { id: 'inById', label: 'In By ID', visible: false, order: 24 },
        { id: 'outById', label: 'Out By ID', visible: false, order: 25 },
        { id: 'activeById', label: 'Active By ID', visible: false, order: 26 },
        // Storage details from nested storage object
        { id: 'storageCode', label: 'Storage Code', visible: false, order: 27 },
        { id: 'storageSize', label: 'Storage Size', visible: false, order: 28 },
        {
            id: 'storageTypeId',
            label: 'Storage Type ID',
            visible: false,
            order: 29,
        },
        { id: 'isPart', label: 'Is Part', visible: false, order: 30 },
        // Warehouse details from nested warehouse object
        {
            id: 'warehouseName',
            label: 'Warehouse Name',
            visible: false,
            order: 31,
        },
        {
            id: 'warehouseSize',
            label: 'Warehouse Size (sqm)',
            visible: false,
            order: 32,
        },
        // Location details from nested location object
        {
            id: 'locationPath',
            label: 'Location Path',
            visible: false,
            order: 33,
        },
        // User details from nested storage.user object
        {
            id: 'customerName',
            label: 'Customer Name',
            visible: false,
            order: 34,
        },
        { id: 'customerId', label: 'Customer ID', visible: false, order: 35 },
    ];

    const renderColumn = (columnId: string) => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'type':
                return (
                    <BoxitChipField
                        key={columnId}
                        record
                        source="type"
                        list="storage_record_type"
                    />
                );
            case 'order':
                return (
                    <FunctionField
                        key={columnId}
                        label="Order"
                        render={record => <OrderField record={record} />}
                    />
                );
            case 'requestId':
                return (
                    <FunctionField
                        key={columnId}
                        label="Request ID"
                        render={record => <RequestField record={record} />}
                    />
                );
            case 'storage':
                return (
                    <FunctionField
                        key={columnId}
                        label="Storage"
                        render={record => <StorageField record={record} />}
                    />
                );
            case 'warehouse':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse"
                        render={record => <WarehouseField record={record} />}
                    />
                );
            case 'location':
                return (
                    <FunctionField
                        key={columnId}
                        label="Location"
                        render={record => <LocationField record={record} />}
                    />
                );
            case 'from':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="from"
                        label="IN"
                        showTime
                    />
                );
            case 'to':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="to"
                        label="OUT"
                        showTime
                    />
                );
            case 'status':
                return (
                    <BoxitChipField
                        key={columnId}
                        record
                        source="status"
                        list="storage_record"
                    />
                );
            case 'qty':
                return (
                    <TextField key={columnId} source="qty" label="Quantity" />
                );
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="createdAt"
                        showTime
                    />
                );
            case 'updatedAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="updatedAt"
                        showTime
                    />
                );
            case 'activeAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="activeAt"
                        label="Stored At"
                        showTime
                    />
                );
            case 'storageId':
                return <TextField key={columnId} source="storageId" />;
            case 'orderId':
                return <TextField key={columnId} source="orderId" />;
            case 'warehouseId':
                return <TextField key={columnId} source="warehouseId" />;
            case 'locationId':
                return <TextField key={columnId} source="locationId" />;
            case 'countryId':
                return <TextField key={columnId} source="countryId" />;
            case 'inBy':
                return (
                    <FunctionField
                        key={columnId}
                        label="In By"
                        render={record => <InByField record={record} />}
                    />
                );
            case 'outBy':
                return (
                    <FunctionField
                        key={columnId}
                        label="Out By"
                        render={record => <OutByField record={record} />}
                    />
                );
            case 'storedBy':
                return (
                    <FunctionField
                        key={columnId}
                        label="Stored By"
                        render={record => <StoredByField record={record} />}
                    />
                );
            case 'inById':
                return <TextField key={columnId} source="inById" />;
            case 'outById':
                return <TextField key={columnId} source="outById" />;
            case 'activeById':
                return <TextField key={columnId} source="activeById" />;
            case 'storageCode':
                return (
                    <FunctionField
                        key={columnId}
                        label="Storage Code"
                        render={record => record.storage?.code || '-'}
                    />
                );
            case 'storageSize':
                return (
                    <FunctionField
                        key={columnId}
                        label="Storage Size"
                        render={record => record.storage?.size || '-'}
                    />
                );
            case 'storageTypeId':
                return (
                    <FunctionField
                        key={columnId}
                        label="Storage Type ID"
                        render={record => record.storage?.storagetypeId || '-'}
                    />
                );
            case 'isPart':
                return (
                    <FunctionField
                        key={columnId}
                        label="Is Part"
                        render={record =>
                            record.storage?.isPart ? 'Yes' : 'No'
                        }
                    />
                );
            case 'warehouseName':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse Name"
                        render={record =>
                            record.warehouse?.name ||
                            record.warehouseName ||
                            '-'
                        }
                    />
                );
            case 'warehouseSize':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse Size (sqm)"
                        render={record =>
                            record.warehouse?.size_sqm ||
                            record.warehouseSize_sqm ||
                            '-'
                        }
                    />
                );
            case 'locationPath':
                return (
                    <FunctionField
                        key={columnId}
                        label="Location Path"
                        render={record =>
                            record.location?.path || record.locationPath || '-'
                        }
                    />
                );
            case 'customerName':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Name"
                        render={record => record.storage?.user?.name || '-'}
                    />
                );
            case 'customerId':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer ID"
                        render={record => record.storage?.user?.id || '-'}
                    />
                );
            case 'show':
                return <ShowButton key={columnId} />;
            default:
                return <TextField key={columnId} source={columnId} />;
        }
    };

    const combinedProps = {
        title: 'Storage Records',
        bulkActionButtons: false,
        filters: <StorageRecordFilter />,
        sort: { field: 'id', order: 'DESC' },
        defaultColumns: allColumns,
        storageKey: 'storageRecordsList',
        renderColumn,
        filtersComponent: <StorageRecordFilter />,
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'storage-records',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

StorageRecordList.defaultProps = {
    resource: 'storage-records',
    storagerecord: { search: '' },
};

export default StorageRecordList;
